import axios from 'axios';
import { CourseModel, UserModel, ClassroomModel } from '@descomplica/dex-models';
import { ENDPOINTS } from '@/settings/endpoints';
import { showPostgradErrorToast } from '../postgradErrorToast';
import Analytics from '../analytics';
import { EVENT_LABELS } from '../analytics/labels';
import { getCourseCategory } from '@/helpers/category';
import i18n from '@/i18n';

export interface Urls {
  pdfUrl: string;
  imageUrl: string;
}

interface GetMicrocertificateUrlResponseType {
  data: Urls;
}

interface GetMicrocertificateEligibilityResponseType {
  data: {
    eligible: boolean;
  };
}

export interface ShareMicrocertificateOnLinkedinRequestBody {
  linkedinOAuthCode: string;
  redirectUri: string;
  content: string;
  visibility: string;
  postCategory: 'IMAGE';
  imageSource: string;
}

enum EligibilityStrategy {
  NAME = 'name',
  PROGRESS = 'progress',
}

const MICROCERTIFICATE_URL_QUERY_ERROR_MESSAGES: { [errorIdentifier: string]: string } = {
  GetMicrocertificateUrlUserWithIncompleteData: i18n
    .t('postgradMicrocertificate.errorMessages.inconsistentUserData')
    .toString(),
};

export const getMicrocertificateUrlQueryErrorMessage = (
  errorIdentifier: keyof typeof MICROCERTIFICATE_URL_QUERY_ERROR_MESSAGES,
): string => {
  return (
    MICROCERTIFICATE_URL_QUERY_ERROR_MESSAGES[errorIdentifier] ||
    i18n.t('postgradMicrocertificate.errorMessages.downloadGenericError').toString()
  );
};

export default class MicrocertificateRequestsService {
  static async getMicrocertificateUrl({
    user,
    course,
    classroom,
    token,
  }: {
    user: UserModel;
    course: CourseModel;
    classroom: ClassroomModel;
    token: string;
  }): Promise<Urls | null> {
    try {
      const response = await axios.get<GetMicrocertificateUrlResponseType>(
        '/microcertificate/url',
        {
          baseURL: ENDPOINTS.pegasusMustang.POSTGRAD_PROGRESS as string,
          params: {
            studentId: user.id,
            disciplineId: classroom.id,
            disciplineName: classroom.name,
            courseId: course.id,
            courseName: course.name,
          },
          headers: {
            Authorization: `Bearer ${token}`,
            'Cache-Control': 'no-cache',
            Pragma: 'no-cache',
            Expires: '0',
          },
        },
      );

      const { pdfUrl, imageUrl } = response.data.data;
      return { pdfUrl, imageUrl };
    } catch (error: any) {
      const errorIdentifier = error.response.data?.identifier ?? 'UnknownResponseError';

      const toastMessage = getMicrocertificateUrlQueryErrorMessage(errorIdentifier);

      showPostgradErrorToast(error, toastMessage, { longMessage: true });

      Analytics.trackEvent(EVENT_LABELS.postgradMicrocertificateDownloadError, {
        error_identifier: errorIdentifier,
        course: course.name,
        category: getCourseCategory(course),
      });
    }

    return null;
  }

  static async getMicrocertificateEligibility({
    classroomId,
    course,
    token,
  }: {
    classroomId: number;
    course: CourseModel;
    token: string;
  }): Promise<Boolean> {
    try {
      const response = await axios.get<GetMicrocertificateEligibilityResponseType>(
        '/microcertificate/eligibility',
        {
          baseURL: ENDPOINTS.pegasusMustang.POSTGRAD_PROGRESS as string,
          params: {
            strategy: EligibilityStrategy.PROGRESS,
            classroomId,
          },
          headers: {
            Authorization: `Bearer ${token}`,
            'Cache-Control': 'no-cache',
            Pragma: 'no-cache',
            Expires: '0',
          },
        },
      );

      return response.data.data.eligible;
    } catch (error: any) {
      showPostgradErrorToast(
        error,
        i18n.t('postgradMicrocertificate.errorMessages.eligibilityGenericError').toString(),
        { longMessage: true },
      );

      Analytics.trackEvent(EVENT_LABELS.postgradMicrocertificateEligibilityError, {
        error_identifier: error.response.data?.identifier || 'UnknownResponseError',
        course: course.name,
        category: getCourseCategory(course),
      });

      return false;
    }
  }

  static async getMicrocertificateNameEligibility({
    course,
    token,
  }: {
    course: CourseModel;
    token: string;
  }): Promise<boolean> {
    try {
      const response = await axios.get<GetMicrocertificateEligibilityResponseType>(
        '/microcertificate/eligibility',
        {
          baseURL: ENDPOINTS.pegasusMustang.POSTGRAD_PROGRESS as string,
          params: {
            strategy: EligibilityStrategy.NAME,
          },
          headers: {
            Authorization: `Bearer ${token}`,
            'Cache-Control': 'no-cache',
            Pragma: 'no-cache',
            Expires: '0',
          },
        },
      );

      return response.data.data.eligible;
    } catch (error: any) {
      showPostgradErrorToast(
        error,
        i18n.t('postgradMicrocertificate.errorMessages.eligibilityGenericError').toString(),
        { longMessage: true },
      );

      Analytics.trackEvent(EVENT_LABELS.postgradMicrocertificateEligibilityError, {
        error_identifier: error.response.data?.identifier || 'UnknownResponseError',
        course: course.name,
        category: getCourseCategory(course),
      });

      return false;
    }
  }

  static async handleClassroomProgress(
    dexToken: string,
    payload: {
      email: string;
      oldClassroomProgress: number;
      classroomId: number;
      classroomName: string;
      classroomUrl: string;
    },
  ): Promise<void> {
    const { email, classroomId, classroomName, classroomUrl, oldClassroomProgress } = payload;

    await axios.post(
      '/notify',
      {
        email,
        classroomId,
        disciplineName: classroomName,
        classroomUrl,
        oldProgress: oldClassroomProgress,
      },
      {
        baseURL: ENDPOINTS.pegasusMustang.POSTGRAD_PROGRESS,
        headers: {
          'Authorization-Dex': `Bearer ${dexToken}`,
        },
      },
    );
  }
}
